import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useAuth } from '../context/AuthContext';
import AdminNavbar from './AdminNavbar';
import DualRingSpinner from './DualRingSpinner';

/* ---- moria admin design tokens — "Quiet Atelier" ---------------------- */
const AdminTheme = createGlobalStyle`
  :root {
    --adm-sage:        #5C6C4B;   /* brand primary  */
    --adm-sage-soft:   #788a62;
    --adm-sage-deep:   #3a4531;   /* chrome / header */
    --adm-sage-darker: #2c352343;
    --adm-brass:       #b08d57;   /* metallic accent */
    --adm-clay:        #b5654a;   /* warm alert      */
    --adm-cream:       #f4f1e9;   /* page canvas     */
    --adm-ivory:       #fbf9f3;
    --adm-card:        #ffffff;
    --adm-border:      #e7e1d3;
    --adm-border-soft: #efeadd;
    --adm-ink:         #2a2a24;
    --adm-ink-soft:    #56544a;
    --adm-muted:       #908a78;
    --adm-shadow:      0 1px 2px rgba(58,69,49,.05), 0 12px 32px -18px rgba(58,69,49,.28);
  }
`;

const AdminContainer = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(120% 80% at 100% 0%, rgba(92,108,75,.07) 0%, transparent 55%),
    radial-gradient(90% 60% at 0% 5%, rgba(176,141,87,.06) 0%, transparent 50%),
    var(--adm-cream);
  color: var(--adm-ink);
`;

const AdminContent = styled.main`
  padding-top: 0;
`;

const AccessDenied = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  text-align: center;
  color: var(--adm-clay);
`;

const AccessDeniedTitle = styled.h1`
  margin-bottom: 1rem;
  font-family: 'Goudy Bookletter 1911', serif;
  font-weight: 400;
  letter-spacing: .04em;
  color: var(--adm-ink);
`;

const AccessDeniedMessage = styled.p`
  margin-bottom: 2rem;
  color: var(--adm-muted);
`;

const LoginButton = styled.button`
  padding: 0.85rem 1.75rem;
  background: var(--adm-sage);
  color: #fff;
  border: none;
  border-radius: 2px;
  cursor: pointer;
  font-size: .8rem;
  letter-spacing: .14em;
  text-transform: uppercase;
  text-decoration: none;
  transition: background .2s ease;

  &:hover {
    background: var(--adm-sage-deep);
  }
`;

const AdminLayout = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <AdminContainer>
        <AdminTheme />
        <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'100vh'}}>
          <DualRingSpinner />
        </div>
      </AdminContainer>
    );
  }

  if (!user) {
    return (
      <AdminContainer>
        <AdminTheme />
        <AccessDenied>
          <AccessDeniedTitle>Access Denied</AccessDeniedTitle>
          <AccessDeniedMessage>
            You must be logged in to access the admin portal.
          </AccessDeniedMessage>
          <LoginButton onClick={() => window.location.href = '/login'}>
            Go to Login
          </LoginButton>
        </AccessDenied>
      </AdminContainer>
    );
  }

  if (!user.isAdmin) {
    return (
      <AdminContainer>
        <AdminTheme />
        <AccessDenied>
          <AccessDeniedTitle>Access Denied</AccessDeniedTitle>
          <AccessDeniedMessage>
            You don't have permission to access the admin portal.
          </AccessDeniedMessage>
          <LoginButton onClick={() => window.location.href = '/'}>
            Back to Home
          </LoginButton>
        </AccessDenied>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <AdminTheme />
      <AdminNavbar />
      <AdminContent>
        {children}
      </AdminContent>
    </AdminContainer>
  );
};

export default AdminLayout; 